import SoloProgressStrip from 'src/components/solo/SoloProgressStrip'
import React, { useState } from 'react'

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import Box from '@mui/material/Box'

import Typography from '@mui/material/Typography'
import SvgIcon from '@mui/material/SvgIcon'

import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded'

import EqualizerRoundedIcon from '@mui/icons-material/EqualizerRounded'
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'
import PollCheckDecoration from '@components/common/PollCheckDecoration'
import { err, wait } from 'src/utils/functions'
import { List, ListItem } from 'src/api/types/lists'

import {
  altColorLighter,
  displayFont,
  helperTextColor,
  mainColorLighter,
  shadow2,
} from 'src/ui/theme/tokens'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { GlassCard } from '@components/common/Glass'
import Skeleton from '@mui/material/Skeleton'
import { DailyLeaderboardRow, getDailyLeaderboard, getMyDailyCompletion } from 'src/api/lists'
import { getDailyPoll } from 'src/api/polls'
import { UserSoloStats } from 'src/api/types/users'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import SoloProgressModal from 'src/components/solo/SoloProgressModal'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'

type PlayedInfo = {
  correct: number
  total: number
  guesses: number
  ts: number
  result: 'win' | 'loss'
  rank?: number
  totalParticipants?: number
  topPercentLabel?: string
}

export default function DailyChallengeSection({
  dailyList,
  soloStats,
  userId,
  fullScreen,
  // mode,
  // multiMode,
  onSelectList,
  onStartGame,
  onStartDailyPoll,
  onStartPreviousPoll,
  onNavigateTab,
  sounds,
}: {
  dailyList?: List
  soloStats?: UserSoloStats
  userId?: number
  fullScreen?: boolean
  mode?: string
  multiMode?: string
  onSelectList?: (id: number | undefined, prompt: string, items: ListItem[]) => void
  onStartGame?: () => void
  onStartDailyPoll?: () => void
  onStartPreviousPoll?: () => void
  onNavigateTab?: (tab: 'random' | 'find' | 'polls') => void
  sounds?: { click?: () => void }
}): JSX.Element {
  const [playedInfo, setPlayedInfo] = useState<PlayedInfo | null>(null)
  const [leaderboard, setLeaderboard] = useState<DailyLeaderboardRow[]>([])
  const [leaderboardLoading, setLeaderboardLoading] = useState<boolean>(false)
  const [leaderboardOpen, setLeaderboardOpen] = useState<boolean>(false)
  const [progressOpen, setProgressOpen] = useState<boolean>(false)
  const [dailyVoteSubmitted, setDailyVoteSubmitted] = useState<boolean>(false)
  const [dailyVoteTitle, setDailyVoteTitle] = useState<string>('')
  const [dailyVotePollId, setDailyVotePollId] = useState<number>()
  const [dailyPollSubmissions, setDailyPollSubmissions] = useState<number>(0)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const getDailyResultKey = (listId?: number) => (listId ? `daily_result_${listId}` : '')
  const isSamePlayedInfo = (a: typeof playedInfo | null, b: typeof playedInfo | null): boolean => {
    if (a === b) return true
    if (!a || !b) return false
    return (
      a.correct === b.correct &&
      a.total === b.total &&
      a.guesses === b.guesses &&
      a.ts === b.ts &&
      a.result === b.result &&
      a.rank === b.rank &&
      a.totalParticipants === b.totalParticipants &&
      a.topPercentLabel === b.topPercentLabel
    )
  }

  const loadDailyResult = React.useCallback(
    (listId?: number) => {
      if (userId) {
        if (!listId) {
          setPlayedInfo(null)
          return
        }
        getMyDailyCompletion()
          .then((res) => {
            if (!res.success || !res.data) {
              setPlayedInfo(null)
              return
            }
            const payload = res.data
            if (Number(payload.listId || 0) !== Number(listId)) {
              setPlayedInfo(null)
              return
            }
            const tsRaw = payload.updatedAt || payload.createdAt
            const ts = tsRaw ? new Date(tsRaw).getTime() : Date.now()
            const inferredResult =
              payload.correct >= payload.total && payload.total > 0 ? 'win' : 'loss'
            const nextPlayed: PlayedInfo = {
              correct: Math.max(0, Number(payload.correct || 0)),
              total: Math.max(0, Number(payload.total || 0)),
              guesses: Math.max(0, Number(payload.guesses || 0)),
              ts: Number.isFinite(ts) ? ts : Date.now(),
              result: inferredResult,
              rank: typeof payload.rank === 'number' ? payload.rank : undefined,
              totalParticipants:
                typeof payload.totalParticipants === 'number'
                  ? payload.totalParticipants
                  : undefined,
              topPercentLabel:
                typeof payload.topPercentLabel === 'string' ? payload.topPercentLabel : undefined,
            }
            setPlayedInfo((prev) => (isSamePlayedInfo(prev, nextPlayed) ? prev : nextPlayed))
          })
          .catch((_e) => {
            setPlayedInfo(null)
          })
        return
      }
      if (!listId) {
        setPlayedInfo(null)
        return
      }
      const key = getDailyResultKey(listId)
      const stored = localStorage.getItem(key)
      if (!stored) {
        setPlayedInfo(null)
        return
      }
      try {
        const parsed = JSON.parse(stored)
        const ts = Number(parsed?.ts || 0)
        const correct = Number(parsed?.correct || 0)
        const total = Number(parsed?.total || 0)
        const guesses = Number(parsed?.guesses || 0)
        const inferredResult =
          parsed?.result === 'win'
            ? 'win'
            : parsed?.result === 'loss'
              ? 'loss'
              : total > 0 && correct >= total
                ? 'win'
                : 'loss'
        if (!ts || Date.now() - ts > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(key)
          setPlayedInfo(null)
          return
        }
        const nextPlayed: PlayedInfo = {
          correct: Math.max(0, correct),
          total: Math.max(0, total),
          guesses: Math.max(0, guesses),
          ts,
          result: inferredResult,
          rank: typeof parsed?.rank === 'number' ? parsed.rank : undefined,
          totalParticipants:
            typeof parsed?.totalParticipants === 'number' ? parsed.totalParticipants : undefined,
          topPercentLabel:
            typeof parsed?.topPercentLabel === 'string' ? parsed.topPercentLabel : undefined,
        }
        setPlayedInfo((prev) => (isSamePlayedInfo(prev, nextPlayed) ? prev : nextPlayed))
      } catch {
        setPlayedInfo((prev) => (prev === null ? prev : null))
      }
    },
    [setPlayedInfo, userId]
  )

  React.useEffect(() => {
    loadDailyResult(dailyList?.id)
  }, [dailyList?.id, loadDailyResult])

  const fetchLeaderboard = React.useCallback((offset = 0, append = false) => {
    setLeaderboardLoading(true)
    getDailyLeaderboard({ limit: 110, offset })
      .then((res) => {
        if (res.success && res.data) {
          setLeaderboard(append ? (prev) => prev.concat(res.data || []) : res.data)
        }
      })
      .catch((e) => err(e))
      .finally(() => {
        setLeaderboardLoading(false)
      })
  }, [])

  React.useEffect(() => {
    fetchLeaderboard(0, false)
  }, [fetchLeaderboard, dailyList?.id])

  const getGuestVoteKey = (pollId: number | undefined) => {
    if (!pollId) return ''
    return `poll_guest_key_${pollId}`
  }

  React.useEffect(() => {
    let current = true
    setDailyVotePollId(undefined)
    Promise.all([getDailyPoll({ type: 'today' }), getDailyPoll({ type: 'previous' })])
      .then(([todayRes, previousRes]) => {
        if (!current) return
        const todayPoll = todayRes.success ? todayRes.data : undefined
        const previousPoll = previousRes.success ? previousRes.data : undefined
        const guestSubmitted =
          !userId && !!todayPoll?.id ? !!localStorage.getItem(getGuestVoteKey(todayPoll.id)) : false
        setDailyVoteSubmitted(Boolean(todayPoll?.viewerSubmitted || guestSubmitted))
        setDailyVoteTitle(String(todayPoll?.prompt || ''))
        setDailyVotePollId(todayPoll?.id)
        setDailyPollSubmissions(Number(previousPoll?.submissions || 0))
      })
      .catch(() => {
        if (!current) return
        setDailyVoteSubmitted(false)
        setDailyVoteTitle('')
        setDailyPollSubmissions(0)
      })
    return () => {
      current = false
    }
  }, [userId])

  const formatDuration = (ms?: number) => {
    if (!ms || ms <= 0) return '--'
    const totalSeconds = Math.min(3600, Math.floor(ms / 1000))
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}m ${seconds}s`
  }

  const buildDailyDisplayRows = React.useCallback(
    (rows: DailyLeaderboardRow[], meRank?: number, meUserId?: number) => {
      const useDummy = false
      const total = rows.length
      const inferredMeRank = meRank && meRank > 0 ? meRank : Math.min(150, total)

      const baseRows: DailyLeaderboardRow[] = rows.slice()

      // baseRows.sort((a, b) => {
      //   if (a.correct !== b.correct) return b.correct - a.correct
      //   if (a.guesses !== b.guesses) return b.guesses - a.guesses
      //   return b.completionMs - a.completionMs
      // })

      // baseRows.forEach((row, idx) => {
      //   row.rank = idx + 1
      // })

      const meIndex = meUserId
        ? baseRows.findIndex((row) => row.userId === meUserId)
        : baseRows.findIndex((row) => row.rank === inferredMeRank)
      const safeMeIndex = meIndex >= 0 ? meIndex : Math.min(baseRows.length - 1, inferredMeRank - 1)
      const aroundStart = Math.max(0, safeMeIndex - 50)
      const aroundEnd = Math.min(baseRows.length, safeMeIndex + 51)

      const top = baseRows.slice(0, Math.min(10, baseRows.length))
      const around = baseRows.slice(aroundStart, aroundEnd)

      const mergedRanks = new Set<number>()
      const display: Array<
        { type: 'row'; row: DailyLeaderboardRow; isMe?: boolean } | { type: 'divider'; key: string }
      > = []

      top.forEach((row) => {
        mergedRanks.add(row.rank)
        display.push({
          type: 'row',
          row,
          isMe: meUserId ? row.userId === meUserId : row.rank === inferredMeRank,
        })
      })

      if (around.length && (around[0]?.rank || 0) > (top[top.length - 1]?.rank || 0) + 1) {
        display.push({ type: 'divider', key: 'divider-top' })
      }

      around.forEach((row) => {
        if (mergedRanks.has(row.rank)) return
        mergedRanks.add(row.rank)
        display.push({
          type: 'row',
          row,
          isMe: meUserId ? row.userId === meUserId : row.rank === inferredMeRank,
        })
      })

      return { display, useDummy }
    },
    []
  )

  const dailyDisplay = React.useMemo(
    () => buildDailyDisplayRows(leaderboard, playedInfo?.rank, userId),
    [buildDailyDisplayRows, leaderboard, playedInfo?.rank, userId]
  )

  const dailyEntryCount = dailyDisplay.useDummy ? 300 : leaderboard.length
  const myRowRef = React.useRef<HTMLTableRowElement | null>(null)
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!leaderboardOpen) return
    let frame = 0
    let timeout: number | undefined
    const maxTries = 12
    const attempt = () => {
      const container = tableContainerRef.current
      const row = myRowRef.current
      if (container && row) {
        const offset = row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2
        container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' })
        return
      }
      if (frame < maxTries) {
        frame += 1
        timeout = window.setTimeout(() => requestAnimationFrame(attempt), 80)
      }
    }
    timeout = window.setTimeout(() => requestAnimationFrame(attempt), 120)
    return () => {
      if (timeout) window.clearTimeout(timeout)
    }
  }, [leaderboardOpen, dailyDisplay.display.length])

  return (
    <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2, pt: soloStats ? 1 : 2, pb: 4 }}>
      {soloStats && (
        <SoloProgressStrip
          stats={soloStats}
          onOpen={() => {
            sounds?.click?.()
            setProgressOpen(true)
          }}
        />
      )}
      <GlassCard
        className="rt-daily-hero"
        sx={{
          position: 'relative',
          p: { xs: 2, sm: 3 },
          background: '#193b58',
          borderColor: '#335770',
          color: '#ffffff',
          textAlign: 'left',
          boxShadow: '0 4px 0 #bfd4e3, 0 12px 25px #183d5610',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -40,
            top: -55,
            width: 240,
            height: 240,
            borderRadius: '50%',
            border: '34px solid #53d9f510',
            pointerEvents: 'none',
          }}
        />
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography
            variant="overline"
            sx={{ color: '#a5d9ed', fontSize: 10, letterSpacing: '.08em', fontWeight: 700 }}
          >
            TODAY&apos;S BRAIN BOOST
          </Typography>
          {!playedInfo && (
            <Chip
              size="small"
              label="+100 XP"
              sx={{
                background: '#ffe178',
                color: '#664f19',
                transform: 'rotate(4deg)',
                height: 24,
                fontSize: 10,
                '& .MuiChip-label': { px: 1 },
                borderRadius: '12px',
                boxShadow: '0 3px 0 #a28c42',
              }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} mt={1}>
          <Box
            component="img"
            src="/brand/podium.svg"
            alt=""
            aria-hidden="true"
            sx={{ width: { xs: 56, sm: 104 }, flexShrink: 0 }}
          />
          <Box flex={1} minWidth={0}>
            <Typography
              variant="h2"
              sx={{ fontFamily: displayFont, color: 'inherit', fontSize: { xs: 26, sm: 36 } }}
            >
              Daily Challenge
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: '#b9d4e5', mt: 0.5, fontSize: 13 }}>
          Ten ranks. Five guesses. One daily shot.
        </Typography>
        <Box sx={{ borderTop: '1px solid #ffffff22', mt: 1, pt: 1.5 }}>
          {dailyList ? (
            <Typography
              variant="h5"
              sx={{ color: '#ffffff', fontSize: { xs: 20, sm: 24 }, mb: 0.5 }}
            >
              {dailyList.prompt}
            </Typography>
          ) : (
            <Skeleton width="75%" height={36} sx={{ bgcolor: '#ffffff25' }} />
          )}
          <Typography variant="body2" sx={{ color: '#b9d4e5', fontSize: 13, mb: 1.5 }}>
            {playedInfo
              ? 'You took on today’s challenge. Nice work!'
              : dailyEntryCount
                ? 'Join the ' + dailyEntryCount + ' other players!'
                : 'Be the first to take on today’s challenge.'}
          </Typography>
          {playedInfo ? (
            <Chip
              color="primary"
              label={
                playedInfo.result === 'win'
                  ? 'Ranked in ' + playedInfo.guesses + ' guesses!'
                  : playedInfo.correct + '/' + playedInfo.total + ' items ranked'
              }
              sx={{
                mb: 2,
                background: '#e1f5fb',
                color: '#193b58',
                border: '1px solid #b9dce9',
                fontWeight: 700,
              }}
            />
          ) : null}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap={1}
            flexWrap="wrap"
          >
            <Button
              variant="contained"
              disabled={!dailyList}
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{
                flex: '0 0 auto',
                minHeight: 48,
                fontSize: 15,
              }}
              onClick={() => {
                if (!dailyList) return
                onSelectList?.(dailyList.id, dailyList.prompt, dailyList.items || [])
                wait(100)
                  .then(() => onStartGame?.())
                  .catch((e) => err(e))
              }}
            >
              {playedInfo ? 'Play for fun' : 'Play Daily Challenge'}
            </Button>
            <Button
              variant="text"
              startIcon={<LeaderboardRoundedIcon />}
              onClick={() => {
                sounds?.click?.()
                setLeaderboardOpen(true)
              }}
              sx={{
                color: '#c0d8e8',
                fontSize: 13,
                minHeight: 40,
                '@media (hover: hover) and (pointer: fine)': {
                  '&:hover': { background: '#ffffff0d' },
                },
              }}
            >
              Leaderboard ({dailyEntryCount})
            </Button>
          </Box>
          {playedInfo ? (
            <Typography variant="caption" sx={{ color: '#b9d4e5', display: 'block', mt: 1.5 }}>
              Already participated. Replays won’t change your score or XP.
            </Typography>
          ) : null}
        </Box>
      </GlassCard>
      <GlassCard
        className="rt-daily-poll"
        sx={{
          mt: 2,
          p: { xs: 2, sm: 3 },
          background: '#ffefdd',
          borderColor: '#efddc9',
          boxShadow: '0 3px 0 #efddc9',
          textAlign: 'left',
          position: 'relative',
          overflow: 'hidden',
          isolation: 'isolate',
          '& > :not(.rt-poll-decoration)': { position: 'relative', zIndex: 1 },
        }}
      >
        <PollCheckDecoration />
        <Typography
          variant="overline"
          color="secondary.dark"
          sx={{
            display: 'block',
            fontSize: 10,
            lineHeight: 1.5,
            letterSpacing: '.08em',
            fontWeight: 700,
          }}
        >
          YOUR DAILY VOTE
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} mt={1}>
          <Box
            component="img"
            src="/brand/daily-poll.svg"
            alt=""
            aria-hidden="true"
            sx={{ width: { xs: 56, sm: 104 }, height: 'auto', flexShrink: 0 }}
          />
          <Box flex={1} minWidth={0}>
            <Typography
              variant="h2"
              sx={{ fontFamily: displayFont, color: '#693413', fontSize: { xs: 26, sm: 36 } }}
            >
              Daily Poll
            </Typography>
          </Box>
        </Box>
        <Box sx={{ borderTop: '1px solid #e8cdb2', mt: 1.5, pt: 1.5 }}>
          {dailyVoteTitle ? (
            <Typography
              variant="h5"
              sx={{ fontSize: { xs: 20, sm: 24 }, color: '#693413', mb: 0.5 }}
            >
              {dailyVoteTitle}
            </Typography>
          ) : (
            <Skeleton height={32} width="85%" sx={{ bgcolor: '#ffdfc2' }} />
          )}
          <Typography variant="body2" color="#855c3d" fontSize={13} mb={1.5}>
            {dailyVoteSubmitted
              ? 'Your vote is in! View or update it below.'
              : 'Vote today. See the community’s picks tomorrow.'}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
            gap={1.5}
          >
            <Button
              variant="contained"
              color="secondary"
              startIcon={<HowToVoteRoundedIcon />}
              disabled={!dailyVotePollId || !onStartDailyPoll}
              sx={{
                minHeight: 48,
                flexShrink: 0,
                '&.Mui-disabled': {
                  background: '#f4d2b5',
                  color: '#987457',
                  borderColor: '#dfb58f',
                  boxShadow: '0 2px 0 #d9ac87',
                  '& .MuiSvgIcon-root': { color: 'inherit' },
                },
              }}
              onClick={() => {
                sounds?.click?.()
                onStartDailyPoll?.()
              }}
            >
              {dailyVoteSubmitted ? 'View your vote' : 'Submit Vote'}
            </Button>
            <Chip
              size="small"
              icon={<EqualizerRoundedIcon />}
              label={'Yesterday’s results (' + dailyPollSubmissions + ')'}
              onClick={() => {
                sounds?.click?.()
                onStartPreviousPoll?.()
              }}
              sx={{
                maxWidth: '100%',
                background: 'transparent',
                color: '#944217',
                border: 0,
                boxShadow: 'none',
                '& .MuiChip-icon': { color: '#944217' },
                '&:hover': { background: 'transparent' },
                '@media (hover: hover) and (pointer: fine)': {
                  '&:hover': { background: '#e9854914' },
                },
              }}
            />
          </Box>
        </Box>
      </GlassCard>
      <Box textAlign="center" mt={3}>
        <Typography variant="body2" color="text.secondary">
          A little more curious?
        </Typography>
        <Box display="flex" justifyContent="center" gap={0.5} mt={0.5} flexWrap="wrap">
          {(
            [
              ['Random', 'random'],
              ['Browse Trivia', 'find'],
              ['More Polls', 'polls'],
            ] as const
          ).map(([label, tab]) => (
            <Button
              key={tab}
              size="small"
              onClick={() => {
                sounds?.click?.()
                onNavigateTab?.(tab)
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>
      <Dialog
        open={leaderboardOpen}
        onClose={() => setLeaderboardOpen(false)}
        fullScreen={isSmallScreen}
        fullWidth
        maxWidth="md"
        sx={{
          backdropFilter: 'none',
          '& .MuiDialog-paper': {
            borderRadius: '30px',
            py: 1,
            background: '#e1f5fb',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 1, sm: 1, md: 3 },
            py: 2,
            pt: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
          }}
        >
          <Typography
            fontWeight={600}
            fontSize={{ xs: 26, sm: 30 }}
            sx={{
              width: '100%',
            }}
            textAlign="center"
            component="div"
          >
            <Box display="flex" alignItems="center" gap={1} justifyContent="center">
              <SvgIcon
                fontSize="large"
                sx={{
                  opacity: 0.33,
                }}
              >
                <LeaderboardRoundedIcon />
              </SvgIcon>
              Daily Leaderboard
            </Box>
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            py: 2,
            px: {
              xs: 1,
              sm: 2,
            },
          }}
        >
          <Stack spacing={1.5}>
            <Typography fontWeight={500} variant="body2">
              Results from today&apos;s challenge
            </Typography>
            {/* {playedInfo?.rank && playedInfo?.totalParticipants ? (
              <Box display="flex" gap={1} alignItems="center" justifyContent="center">
                <Typography variant="body2" fontWeight={500} color={helperTextColor}>
                  Rank #{playedInfo.rank}/{playedInfo.totalParticipants}
                </Typography>
                {playedInfo.topPercentLabel ? (
                  <>
                    <Typography variant="body2">•</Typography>

                    <Typography variant="body2" fontWeight={500} color={helperTextColor}>
                      {playedInfo.topPercentLabel}
                    </Typography>
                  </>
                ) : null}
              </Box>
            ) : null} */}
            {dailyDisplay.display.length ? (
              <TableContainer
                ref={tableContainerRef}
                sx={{
                  borderRadius: '8px',
                  border: '1px solid rgba(226,232,240,0.9)',
                  background: '#ffffff',
                  maxHeight: { xs: '60vh', sm: '70vh' },
                  overflowY: 'auto',
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          py: 0.5,
                          pl: 1,
                          pr: 0,
                          width: 40,
                        }}
                      >
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5, px: 1 }}>Player</TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          py: 0.5,
                          px: 0.5,
                          width: '40px',
                        }}
                        align="center"
                      >
                        <Typography variant="caption" sx={{ opacity: 0.5 }}>
                          Score
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, py: 0.5, px: 0, width: '47px' }}
                        align="right"
                      >
                        Guesses
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, py: 0.5, px: 1, width: '50px' }}
                        align="right"
                      >
                        Time
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dailyDisplay.display.map((entry, idx) =>
                      entry.type === 'divider' ? (
                        <TableRow key={entry.key + idx}>
                          <TableCell colSpan={5} align="center" sx={{ color: helperTextColor }}>
                            ...
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow
                          key={`daily-lb-${entry.row.rank}-${entry.row.userId}`}
                          ref={entry.isMe ? myRowRef : undefined}
                          sx={{
                            ...(entry.row.correct < 10
                              ? { backgroundColor: altColorLighter }
                              : { backgroundColor: mainColorLighter }),
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: entry.isMe ? 700 : 500,
                              py: 0.5,
                              px: 1,
                            }}
                          >
                            #{entry.row.rank}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: entry.isMe ? 700 : 500,
                              py: 0.5,
                              px: 1,
                              textWrap: 'nowrap',
                            }}
                          >
                            {entry.row.username.slice(0, 20)}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: 0.5,
                                fontWeight: entry.isMe ? 700 : 500,
                              }}
                            >
                              {entry.row.correct}/{entry.row.total}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: entry.isMe ? 700 : 500,
                              }}
                            >
                              {entry.row.guesses}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              py: 0.5,
                              px: 1,
                              whiteSpace: 'nowrap',
                              fontWeight: entry.isMe ? 700 : 500,
                            }}
                          >
                            {formatDuration(entry.row.completionMs)}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : leaderboardLoading ? (
              <Stack spacing={1}>
                <Skeleton variant="text" height={24} />
                <Skeleton variant="text" height={24} />
                <Skeleton variant="text" height={24} />
              </Stack>
            ) : (
              <Typography variant="body2" color={helperTextColor}>
                No leaderboard entries yet for today.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 4, sm: 3, md: 3 },
            py: { xs: 2, md: 3 },
            bgcolor: 'transparent',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%" px={1}>
            <Button
              variant="contained"
              onClick={() => setLeaderboardOpen(false)}
              sx={{
                borderRadius: '999px',
                px: 4,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                background: '#e1f5fb',
                boxShadow: shadow2,
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <SoloProgressModal
        open={progressOpen}
        onClose={() => setProgressOpen(false)}
        soloStats={soloStats}
      />
    </Box>
  )
}
